import React, { useEffect, useRef, useState } from "react";
import EmployeeUtil from 'utils/employee-util'
import _ from "lodash";
import { colorList } from 'utils/colors'
import { AppState, StyleSheet, View } from 'react-native'
import LoadingComponent from 'components/atoms/Loading'
import { useInterval } from 'utils/hooks/use-interval'
import AccountStorage from 'utils/accounts/AccountStorage'
import { storeKeys } from 'services/store/keys'
import { configKeys } from 'utils/config/keys'
import HelperSetting from 'utils/helper-setting'
import { settingsConstants } from 'utils/constants/settings-constants'
import SecureStoreService from 'services/store/secure-store'
import HelperEncryption from 'utils/helper-encryption'
import { TokenStatus } from 'scenes/authentication/components/Token'
import HelperAlerts from 'utils/HelperAlerts'
import i18n from 'utils/i18n'
import { appRestart } from 'utils/helper-restart'
import ThemedQrCode from 'components/atoms/ThemedQrCode'
import { configClient } from 'components/provider/ConfigProvider'

const QRcode = ( { onTokenStatusChange, onNameChange } = {} ) => {

	const [ qrcode, setQrcode ] = useState( null );
	const [ isLoading, setIsLoading ] = useState( false );

	const mounted = useRef( true );
	const fetching = useRef( false );
	const validating = useRef( false );
	const completed = useRef( false );
	const currentCode = useRef( null );
	const [ isComplete, setIsComplete ] = useState( false );
	const [ appState, setAppState ] = useState( AppState.currentState );

	const getQrCode = () => {
		if( !mounted.current || completed.current || fetching.current || validating.current ) return;
		fetching.current = true;
		EmployeeUtil.getQrCode({
			onStart: setIsLoading.bind( this, true ),
			onSuccess: gotQrCode,
			onError: gotQrCodeError
		});
	}

	const gotQrCode = ( result ) => {
		fetching.current = false;
		if( !mounted.current || completed.current ) return;
		const status = _.get( result, 'status' );
		const code = _.get( result, 'code' );

		setIsLoading( false );

		if( status === 'success' ) {
			currentCode.current = code;
			setQrcode( code );
		}else{
			currentCode.current = null;
			setQrcode( null );
		}
	}

	const gotQrCodeError = ( result ) => {
		fetching.current = false;
		if( !mounted.current || completed.current ) return;
		setIsLoading( false );
	}

	const validateQrCode = () => {
		if( !mounted.current || completed.current || validating.current || fetching.current || currentCode.current === null ) return;
		validating.current = true;
		const code = currentCode.current;
		EmployeeUtil.validateQrCode({
			code,
			onSuccess: result => {
				validating.current = false;
				if( mounted.current && !completed.current && currentCode.current === code ) validateQrCodeSuccess( result );
			},
			onError: result => {
				validating.current = false;
				if( mounted.current && !completed.current && currentCode.current === code ) validateQrCodeError( result );
			},
		});
	}

	const validateQrCodeSuccess = ( result ) => {
		let status = _.get( result, 'status' );
		if( status === 'success' ){
			completed.current = true;
			setIsComplete( true );
			const authorisation = _.get( result, 'authorisation', null );
			const encryption_key = _.get( result, 'key', null );
			const guid = _.get( result, 'guid', null );

			onNameChange( _.get( result, 'name', ''  ) );

			// create a token for all this
			AccountStorage.createNewIdentifier().then( ( account_identifier ) => {
				let accountData = {
					[ storeKeys.ACCOUNT_IDENTIFIER ]: account_identifier,
					[ storeKeys.EMPLOYEE_GUID ]: guid,
					[ storeKeys.ENCRYPTION_KEY ]: encryption_key,
					[ storeKeys.EMPLOYEE_SESSION ]: authorisation,
					[ storeKeys.DOMAIN ]: configClient.get( configKeys.CUSTOMER_ID ),
					[ storeKeys.DOMAIN_PREFIX ]: configClient.getApiCustomerIdPrefix(),
					[ storeKeys.EMPLOYEE_NAME ]: _.get( result, 'name', '' ),
					[ storeKeys.COMPANY_NAME ]: HelperSetting.get( settingsConstants.COMPANY_NAME )
				}

				return AccountStorage.storeAccount( account_identifier, accountData ).then( () => {
					HelperEncryption.setKey( encryption_key );
					
					return SecureStoreService.setMulti({
						[ storeKeys.EMPLOYEE_GUID ] : guid,
						[ storeKeys.ENCRYPTION_KEY ] : encryption_key,
						[ storeKeys.ACCOUNT_IDENTIFIER ]: account_identifier,
						[ storeKeys.EMPLOYEE_SESSION] : authorisation,
						[ storeKeys.DOMAIN ]: configClient.get( configKeys.CUSTOMER_ID ),
						[ storeKeys.DOMAIN_PREFIX ]: configClient.getApiCustomerIdPrefix()
					}).then( () => {
						onTokenStatusChange( TokenStatus.VERIFIED );
						
						setTimeout( appRestart, 2000 );
					});
				});
			}).catch( () => {
				if( !mounted.current ) return;
				completed.current = false;
				setIsComplete( false );
				currentCode.current = null;
				setQrcode( null );
				HelperAlerts.alert(
					i18n.t( 'pages.more.accounts.save_error_title' ),
					i18n.t( 'pages.more.accounts.save_error' ),
				);
			});
		}
	}

	const validateQrCodeError = result => {
		HelperAlerts.alert(
			i18n.t('page.authentication.domain.alerts.something_went_wrong.title'),
			i18n.t('page.authentication.domain.alerts.something_went_wrong.message')
		)
	}

	useEffect( () => {
		mounted.current = true;
		getQrCode();
		const subscription = AppState.addEventListener( 'change', setAppState );
		return () => {
			mounted.current = false;
			subscription.remove();
		};
	}, [] );

	useInterval( validateQrCode, !isComplete && appState === 'active' ? 2000 : null );

	useInterval( getQrCode, !isComplete && appState === 'active' ? 30000 : null );

	const items = [];
	if( !isLoading && qrcode !== null ){
		items.push(
			<ThemedQrCode
				key="qr-code"
				value={ qrcode }
				size={ 200 }
				color={ colorList.BLACK.primary }
				backgroundColor={ colorList.WHITE.primary}
			/>
		)
	}

	if( isLoading ) {
		items.push( <LoadingComponent key="loading" size={ 'large' } /> );
	}

	return (
		<View style={ styles.qrContainer}>
			{ items }
		</View>
	);
}

export default QRcode;

const styles = StyleSheet.create({
	qrContainer: {
		width: '100%',
		maxWidth: 250,
		height: 200,
		flexBasis: 200,
		flexGrow: 0,
		flexShrink: 0,
		minHeight: 200,
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
	},
})
